import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  ArcElement,
  Tooltip,
  Legend
);

const TravelExpensesCalculator = () => {
  const [travelData, setTravelData] = useState({
    transportation: '',
    accommodation: '',
    food: '',
    activities: '',
    miscellaneous: '',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTravelData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setTravelData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculateTotal = () => {
    const { transportation, accommodation, food, activities, miscellaneous } = travelData;

    if (!transportation || !accommodation || !food || !activities || !miscellaneous) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const trans = parseFloat(transportation);
      const accom = parseFloat(accommodation);
      const foodCost = parseFloat(food);
      const act = parseFloat(activities);
      const misc = parseFloat(miscellaneous);

      if (isNaN(trans) || isNaN(accom) || isNaN(foodCost) || isNaN(act) || isNaN(misc)) {
        setError('Please enter valid numbers for all fields');
        return;
      }

      const total = trans + accom + foodCost + act + misc;

      const generateChartData = () => {
        return {
          labels: ['Transportation', 'Accommodation', 'Food', 'Activities', 'Miscellaneous'],
          datasets: [
            {
              data: [trans, accom, foodCost, act, misc],
              backgroundColor: [
                'rgba(255, 99, 132, 0.5)',
                'rgba(54, 162, 235, 0.5)',
                'rgba(255, 206, 86, 0.5)',
                'rgba(75, 192, 192, 0.5)',
                'rgba(153, 102, 255, 0.5)'
              ],
              borderColor: [
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)',
                'rgba(255, 206, 86, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(153, 102, 255, 1)'
              ],
              borderWidth: 1
            }
          ]
        };
      };

      setResults({
        transportation: trans.toFixed(2),
        accommodation: accom.toFixed(2),
        food: foodCost.toFixed(2),
        activities: act.toFixed(2),
        miscellaneous: misc.toFixed(2),
        total: total.toFixed(2)
      });
      setChartData(generateChartData());

    } catch (error) {
      setError('Please enter valid numbers for all fields');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Travel Expenses Calculator | Trip Budget Planner</title>
        <meta name="description" content="Plan your trip budget with our comprehensive travel expenses calculator. Estimate costs for transportation, accommodation, food, and activities." />
        <meta name="keywords" content="travel calculator, trip budget planner, expense tracker, vacation calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-green-600 mr-4">🧳</div>
            <h1 className="text-3xl font-bold text-gray-800">Travel Expenses Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>This calculator helps you plan your travel budget by estimating expenses across different categories. It provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Comprehensive cost estimation</li>
              <li>✓ Visual breakdown of expenses</li>
              <li>✓ Total budget calculation</li>
              <li>✓ Detailed financial analysis</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transportation:</label>
                <input
                  type="number"
                  id="transportation"
                  name="transportation"
                  value={travelData.transportation}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter transportation costs"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accommodation:</label>
                <input
                  type="number"
                  id="accommodation"
                  name="accommodation"
                  value={travelData.accommodation}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter accommodation costs"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Food:</label>
                <input
                  type="number"
                  id="food"
                  name="food"
                  value={travelData.food}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter food costs"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activities:</label>
                <input
                  type="number"
                  id="activities"
                  name="activities"
                  value={travelData.activities}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter activities costs"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Miscellaneous:</label>
                <input
                  type="number"
                  id="miscellaneous"
                  name="miscellaneous"
                  value={travelData.miscellaneous}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter miscellaneous costs"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={travelData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Show detailed analysis and expense breakdown
                </label>
              </div>
            </div>

            <button
              onClick={calculateTotal}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Total
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Travel Budget Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Summary</h3>
                    <p className="text-gray-600">
                      Transportation: ${results.transportation}<br />
                      Accommodation: ${results.accommodation}<br />
                      Food: ${results.food}<br />
                      Activities: ${results.activities}<br />
                      Miscellaneous: ${results.miscellaneous}<br />
                      <strong>Total Budget: ${results.total}</strong>
                    </p>
                  </div>

                  {travelData.showDetailedAnalysis && (
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Expense Analysis</h3>
                      <p className="text-gray-600">
                        This breakdown shows the proportion of each expense category in your total travel budget.
                      </p>
                    </div>
                  )}
                </div>

                {chartData && travelData.showDetailedAnalysis && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Pie
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Expense Category Breakdown',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Financial Summary</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Total Budget:</span>
                      ${results.total}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Largest Expense:</span>
                      {Math.max(...Object.values(results)) === results.transportation && 'Transportation'}
                      {Math.max(...Object.values(results)) === results.accommodation && 'Accommodation'}
                      {Math.max(...Object.values(results)) === results.food && 'Food'}
                      {Math.max(...Object.values(results)) === results.activities && 'Activities'}
                      {Math.max(...Object.values(results)) === results.miscellaneous && 'Miscellaneous'}
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Travel Expenses Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to plan your travel budget:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Estimate Transportation Costs</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Include flights, trains, rental cars, or other transportation</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Estimate Accommodation</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Include hotels, Airbnb, or other lodging</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Estimate Food Costs</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Include meals at restaurants and groceries</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 4: Estimate Activities</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Include tours, attractions, and entertainment</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 5: Add Miscellaneous Expenses</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Include souvenirs, emergency funds, and other costs</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 6: Calculate</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Total" to see your results</li>
                  <li>Enable detailed analysis for visual breakdown</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
            <div className="text-gray-600 space-y-4">
              <div>
                <h3 className="font-semibold">How accurate are these estimates?</h3>
                <p>Accuracy depends on your input. Research destination-specific costs for better estimates.</p>
              </div>
              <div>
                <h3 className="font-semibold">Should I include emergency funds?</h3>
                <p>Yes, it's recommended to include a buffer for unexpected expenses.</p>
              </div>
              <div>
                <h3 className="font-semibold">Can I save this calculation for later?</h3>
                <p>Not directly, but you can take screenshots or note down the results for future reference.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">🔢</span></h2>
            <div className="text-gray-600 bg-gray-50 p-6 rounded-lg">
              <p><strong>Inputs:</strong></p>
              <ul className="list-disc pl-6">
                <li>Transportation: $500</li>
                <li>Accommodation: $1200</li>
                <li>Food: $400</li>
                <li>Activities: $300</li>
                <li>Miscellaneous: $200</li>
              </ul>
              <p className="mt-4"><strong>Results:</strong></p>
              <ul className="list-disc pl-6">
                <li>Total Budget: $2600</li>
                <li>Largest Expense: Accommodation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelExpensesCalculator;